import { createContext, useCallback, useContext, useMemo, type JSX, type ReactNode } from 'react';

import en from './en.json';

/**
 * B19 i18n — self-hosted, no-dep translation. Flat key catalog + `t(key, params)`
 * with simple `{param}` interpolation. EN is the base + only complete locale at
 * MVP; locale is resolved from the URL prefix (B18 `/en`). A missing key returns
 * the key itself (fail-soft; visible in dev).
 */

type Catalog = Record<string, string>;
type Params = Record<string, string | number>;
export type TranslateFn = (key: string, params?: Params) => string;

const CATALOGS: Record<string, Catalog> = { en };
const LOCALES = ['en'] as const;
const DEFAULT_LOCALE = 'en';

function resolveLocale(): string {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const seg = window.location.pathname.split('/')[1] ?? '';
  return (LOCALES as readonly string[]).includes(seg) ? seg : DEFAULT_LOCALE;
}

function interpolate(str: string, params?: Params): string {
  if (!params) return str;
  return str.replace(/\{(\w+)\}/g, (_, key: string) => (key in params ? String(params[key]) : `{${key}}`));
}

const I18nContext = createContext<TranslateFn>((key) => key);

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const t = useMemo<TranslateFn>(() => {
    const catalog: Catalog = CATALOGS[resolveLocale()] ?? en;
    return (key, params) => {
      const str = catalog[key];
      return str !== undefined ? interpolate(str, params) : key;
    };
  }, []);
  return <I18nContext.Provider value={t}>{children}</I18nContext.Provider>;
}

/** Returns the `t(key, params?)` translator for the active locale. */
export function useT(): TranslateFn {
  const t = useContext(I18nContext);
  // Stable identity for use in deps.
  return useCallback<TranslateFn>((key, params) => t(key, params), [t]);
}
